// eslint-disable-next-line import/no-unresolved
const { debounce } = window.BAO.Utils

class ProductMediaCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    if (!this.slideEls || this.slideEls.length < 2) return
    this.carousel = this.initialiseCarousel()

    if (this.activeGiftCardVariant) {
      this.updateSlidePosition(this.activeGiftCardVariant)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.dotEls?.forEach(dotEl => {
      this.listeners.add(dotEl, 'click', this.handleDotClick.bind(this))
    })

    this.handleResize = this.handleResize.bind(this)
    const debouncedHandleResize = debounce(this.handleResize, 250)
    this.listeners.add(window, 'resize', debouncedHandleResize.bind(this))

    if (this.thumbnailEl) {
      this.listeners.add(
        this.thumbnailsEl,
        'click',
        this.handleThumbnailClick.bind(this),
      )
    }

    if (this.prevEl) {
      this.listeners.add(this.prevEl, 'click', this.handleArrowClick.bind(this))
    }
    if (this.nextEl) {
      this.listeners.add(this.nextEl, 'click', this.handleArrowClick.bind(this))
    }

    this.giftCardVariants?.forEach(variant => {
      this.listeners.add(variant, 'click', e =>
        this.updateSlidePosition(e.target),
      )
    })
  }

  initialiseCarousel () {
    if (window.innerWidth >= 901 || this.giftCardVariants) {
      this.dataset.autoplay = false
      this.autoplay(false)
    } else {
      if (!this.dataset.isGiftCard) {
        this.dataset.autoplay = this.dragEnabled
        this.autoplay(true)
      }
    }

    // eslint-disable-next-line no-undef
    return new KeenSlider(this.slidesEl, {
      mode: 'snap',
      loop: true,
      drag: this.dragEnabled,
      slides: {
        perView: 1,
        spacing: 0,
      },
      selector: this.els?.slide?.selector,
      created: instance => {
        const currentSlideIndex = instance.track.details.abs
        const currentSlide = instance.slides[currentSlideIndex]
        this.classList.add('prd-MediaCarousel-show')
        if (!currentSlide) return
        if (!this.zoomEl) return
        this.zoomEl.dataset.modalElementFrameKey =
          currentSlide.dataset.modalElementFrameKey
      },
      slideChanged: instance => {
        this.dotEls?.forEach(dotEl => {
          dotEl.setAttribute(
            'aria-current',
            dotEl.dataset.index ===
              (instance?.track?.details?.rel + 1).toString(),
          )
        })

        const currentSlideIndex = this.carousel.track.details.abs
        const currentSlide = this.carousel.slides[currentSlideIndex]
        if (!currentSlide) return
        if (!this.zoomEl) return
        this.zoomEl.dataset.modalElementFrameKey =
          currentSlide.dataset.modalElementFrameKey
      },
      dragStarted: () => {
        this.dataset.autoplay = false
        this.autoplay(false)
      },
    })
  }

  updateZoomEl () {
    const currentSlideIndex = this.carousel.track.details.abs
    const currentSlide = this.carousel.slides[currentSlideIndex]
    if (!currentSlide) return
    this.zoomEl.dataset.modalElementFrameKey =
      currentSlide.dataset.modalElementFrameKey
  }

  handleDotClick (e) {
    this.dotEls?.forEach(dotEl => {
      dotEl.setAttribute(
        'aria-current',
        dotEl.dataset.index === e.target.dataset.index,
      )
    })

    this.carousel.moveToIdx(parseInt(e.target.dataset.index, 10) - 1)
  }

  handleArrowClick (e) {
    const direction = e.target.dataset.productMediaCarouselEl
    if (!direction) return

    if (direction === 'prev') {
      this.carousel.prev()
    } else {
      this.carousel.next()
    }
  }

  handleThumbnailClick (e) {
    const thumbnail = e.target.closest(this.els.thumbnail.selector)

    this.thumbnailEls.forEach(thumbnailEl => {
      thumbnailEl.setAttribute(
        'aria-current',
        thumbnailEl.dataset.index === thumbnail.dataset.index,
      )
    })

    this.carousel.moveToIdx(parseInt(thumbnail.dataset.index, 10) - 1)
  }

  updateSlidePosition (variantEl) {
    const variantType = variantEl.dataset.alt
    const slideToMoveTo = [...this.slideEls].find(
      slide => slide.querySelector('img').getAttribute('alt') === variantType,
    )
    const position = slideToMoveTo.dataset.index

    this.carousel.moveToIdx(parseInt(position) - 1)
    this.autoplay(false)
  }

  handleResize (e) {
    if (window.innerWidth >= 901) {
      this.dataset.autoplay = false
      this.autoplay(false)
    } else {
      this.dataset.autoplay = this.dragEnabled

      if (!this.dataset.isGiftCard) {
        this.autoplay(true)
      }
    }
  }

  autoplay (run) {
    clearInterval(this.interval)

    this.interval = setInterval(() => {
      if (run && this.carousel) {
        this.carousel.next()
      }
    }, 3000)
  }

  get slidesEl () {
    return this.els?.slides?.element
  }

  get slideEls () {
    return this.els?.slide?.elements
  }

  get dotEls () {
    return this.els?.dot?.elements
  }

  get indexEl () {
    return this.els?.index?.element
  }

  get thumbnailsEl () {
    return this.els?.thumbnails?.element
  }

  get thumbnailEls () {
    return this.els?.thumbnail?.elements
  }

  get prevEl () {
    return this.els?.prev?.element
  }

  get nextEl () {
    return this.els?.next?.element
  }

  get zoomEl () {
    return this.els?.zoom?.element
  }

  get giftCardVariants () {
    return this.externalEls?.designVariant?.elements
  }

  get activeGiftCardVariant () {
    return [...this.giftCardVariants].find(el => el.checked)
  }

  get dragEnabled () {
    return this.dataset.dragEnabled === 'true'
  }
}

if (!customElements.get('product-media-carousel')) {
  customElements.define('product-media-carousel', ProductMediaCarousel)
}
